import cx from "classnames";
import React from "react";

import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { StrollerEntity, StrollerPhoto } from "@/mk2/apps/strollers/schemas";
import { strollersDetailUrl, strollersReviewsUrl } from "@/mk2/apps/strollers/urls";
import { Dot } from "@/mk2/components/Dot";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { StarsRating } from "@/mk2/components/StarsRating";
import { url } from "@/mk2/helpers/urls";
import { counter_RATINGS } from "@/mk/autogenerated/translations/MiniStroller.cd45e8fa75409a3e818f8d919256d332";

import styles from "./MiniStroller.mscss";

interface OwnProps {
    className?: string;
    // NOTE: from StrollerEntity - do not use whole interface
    //  define only really needed values. It gives us much more
    //  freedom in tests and stories
    stroller: {
        name: StrollerEntity["name"];
        slug: StrollerEntity["slug"];
        userScore: StrollerEntity["userScore"];
        reviewsCount: StrollerEntity["reviewsCount"];
        previewPhoto: StrollerPhoto;
    };
}

type Props = OwnProps;

export const MiniStroller: React.FunctionComponent<Props> = React.memo(
    ({ className, stroller }) => {
        const strollerPhoto = stroller.previewPhoto || dummyPhoto;

        return (
            <div className={cx(styles.MiniStroller, className)}>
                <Link to={url(strollersDetailUrl, { slug: stroller.slug })}>
                    <div className={styles.MiniStroller__image}>
                        <ImgCropped
                            width={80}
                            height={80}
                            mode={ImgCropMode.Embed}
                            imgWidth={strollerPhoto.originalWidth}
                            imgHeight={strollerPhoto.originalHeight}
                            imgUrl={strollerPhoto.url100x100}
                            alt={stroller.name}
                        />
                    </div>
                </Link>
                <div className={styles.MiniStroller__info}>
                    <Link to={url(strollersDetailUrl, { slug: stroller.slug })}>
                        <span className={styles.MiniStroller__info__name}>{stroller.name}</span>
                    </Link>
                    <div className={styles.MiniStroller__info__reviews}>
                        {stroller.userScore > 0 && (
                            <>
                                <StarsRating
                                    className={styles.MiniStroller__info__reviews__score}
                                    starsCount={stroller.userScore}
                                    canShowHalf
                                />
                                <Dot spaces={1} />
                            </>
                        )}
                        <Link to={url(strollersReviewsUrl, { slug: stroller.slug })}>
                            <Interpolate
                                className={styles.MiniStroller__info__reviews__count}
                                i18nKey={counter_RATINGS}
                                counter={stroller.reviewsCount}
                                count={stroller.reviewsCount}
                            />
                        </Link>
                    </div>
                </div>
            </div>
        );
    },
);

MiniStroller.displayName = "MiniStroller";

interface PlaceholderOwnProps {
    className?: string;
}

export const MiniStrollerPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({
    className,
}) => (
    <div className={cx(styles.MiniStroller, className)}>
        <div className={styles.MiniStroller__image}>
            <LoadingPlaceholder width={80} height={80} />
        </div>
        <div className={styles.MiniStroller__info}>
            <span className={styles.MiniStroller__info__name}>
                <LoadingPlaceholder width={120} />
            </span>
            <div className={styles.MiniStroller__info__reviews}>
                <span className={styles.MiniStroller__info__reviews__score}>
                    <LoadingPlaceholder width={28} height={28} />
                </span>
                <span className={styles.MiniStroller__info__reviews__count}>
                    <LoadingPlaceholder width={80} />
                </span>
            </div>
        </div>
    </div>
);

MiniStrollerPlaceholder.displayName = "MiniStrollerPlaceholder";
